/**
 * JSON-LD Schema Block — Component
 *
 * Injection block that renders a <script type="application/ld+json"> tag
 * with structured data auto-generated from property data and collections.
 *
 * Posts the computed schema to the parent editor via postMessage so the
 * sidebar can show a live JSON preview.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { BlockProps } from '../../types';
import { useBlockToken, useFloorplans, useProperty, useSpecialOffers } from '@vibesites/runtime/react';
import { normalizeMediaUrl } from '@vibesites/ui-primitives';
import { buildFullSchema, type CoreOverrides, type SchemaToggles } from './builders';

function isTruthy(val: string | undefined | null): boolean {
  return val !== 'false' && val !== '0' && val !== '';
}

function normalizeMediaUrls(value: unknown): unknown {
  if (typeof value === 'string') {
    return normalizeMediaUrl(value, { width: 1280 });
  }

  if (Array.isArray(value)) return value.map(normalizeMediaUrls);

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, child]) => [key, normalizeMediaUrls(child)]),
    );
  }

  return value;
}

export function JsonLdSchemaComponent(_props: BlockProps) {
  const property = useProperty();
  const floorplans = useFloorplans();
  const specialOffers = useSpecialOffers(true);

  const schemaTypes = useBlockToken('schema_types', { defaultValue: 'ApartmentComplex,LocalBusiness' });
  const includeReviews = useBlockToken('include_reviews', { defaultValue: 'true' });
  const includeFloorplans = useBlockToken('include_floorplans', { defaultValue: 'true' });
  const includeAmenities = useBlockToken('include_amenities', { defaultValue: 'true' });
  const includeOfficeHours = useBlockToken('include_office_hours', { defaultValue: 'true' });
  const includeGallery = useBlockToken('include_gallery', { defaultValue: 'true' });
  const includeSpecialOffers = useBlockToken('include_special_offers', { defaultValue: 'true' });

  const overrideName = useBlockToken('override_name', { defaultField: 'property_name' });
  const overrideDescription = useBlockToken('override_description', { defaultField: 'property_short_description' });
  const overridePhone = useBlockToken('override_phone', { defaultField: 'property_phone' });
  const overrideUrl = useBlockToken('override_url', { defaultField: 'property_website_url' });
  const overrideImage = useBlockToken('override_image', { defaultField: 'property_featured_image' });

  const customJson = useBlockToken('custom_json', { defaultValue: '' });

  const schema = useMemo(() => {
    if (customJson) {
      try {
        return normalizeMediaUrls(JSON.parse(customJson));
      } catch {
        // Invalid JSON — fall through to auto-generation
      }
    }

    const types = (schemaTypes || 'ApartmentComplex,LocalBusiness')
      .split(',')
      .map((t: string) => t.trim())
      .filter(Boolean);

    const overrides: CoreOverrides = {};
    if (overrideName !== property.property_name) overrides.name = overrideName ?? undefined;
    if (overrideDescription !== property.property_short_description) overrides.description = overrideDescription ?? undefined;
    if (overridePhone !== property.property_phone) overrides.telephone = overridePhone ?? undefined;
    if (overrideUrl !== property.property_website_url) overrides.url = overrideUrl ?? undefined;
    if (overrideImage !== property.property_featured_image) overrides.image = overrideImage ?? undefined;

    const toggles: SchemaToggles = {
      includeReviews: isTruthy(includeReviews),
      includeFloorplans: isTruthy(includeFloorplans),
      includeAmenities: isTruthy(includeAmenities),
      includeOfficeHours: isTruthy(includeOfficeHours),
      includeGallery: isTruthy(includeGallery),
      includeSpecialOffers: isTruthy(includeSpecialOffers),
    };

    return buildFullSchema(property, types, overrides, toggles, {
      floorplans,
      specialOffers,
    });
  }, [
    property, floorplans, specialOffers, schemaTypes, customJson,
    includeReviews, includeFloorplans, includeAmenities,
    includeOfficeHours, includeGallery, includeSpecialOffers,
    overrideName, overrideDescription, overridePhone, overrideUrl, overrideImage,
  ]);

  // Post schema to the editor for sidebar preview
  const postSchema = (data: unknown) => {
    try {
      window.parent.postMessage({ type: 'VIBESITE_JSONLD_SCHEMA_PREVIEW', payload: data }, '*');
    } catch {
      // Fails silently if no parent frame (production)
    }
  };

  // Post immediately whenever schema changes
  useEffect(() => { postSchema(schema); }, [schema]);

  // Also post on an interval so the sidebar receives it if it mounts after the block
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  useEffect(() => {
    const id = setInterval(() => postSchema(schemaRef.current), 2000);
    return () => clearInterval(id);
  }, []);

  const jsonString = JSON.stringify(schema);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonString }}
    />
  );
}
